import {Trans} from '@lingui/macro';
import {isUndefined} from 'lodash';
import React, {FC, useCallback, useMemo, useState} from 'react';
import styled from 'styled-components';

import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useNavigationBlocking} from '../../../../srcCommon/app/navigationBlocking';
import {ApplicationCredentialsModel} from '../../../models/api/applicationCredentialsModel';
import {ApplicationModel} from '../../../models/api/applicationModel';
import {ServerModel, UnsavedServerModel} from '../../../models/api/serverModel';
import {SettingsResourcePagesEnum} from '../../../models/url/settingsUrlModel';
import {SettingsPanelId} from '../../../store/panels/panelStateBase';
import {StyledEditorMain, StyledEditorWrapper} from '../../editors/commons/editorCommons';
import {createServerEditorContext} from '../../editors/serverEditor/serverEditorContext';
import {
  DEFAULT_APPLICATION_CREDENTIALS,
  ServerEditorState,
} from '../../editors/serverEditor/serverEditorState';
import {useServerEditorState} from '../../editors/serverEditor/useServerEditorState';
import {DiscardChangesDialog} from '../../rules/ruleEditor/dialog/discardChangesDialog';
import {ResourceEditorFooter} from '../commons/resourceEditorFooter';
import {ApplicationCredentialsCard} from './cards/applicationCredentialsCard';
import {ServerAuthenticationConfigCard} from './cards/serverAuthenticationConfigCard';
import {ServerOriginInput} from './inputs/serverOriginInput';
import {serverEditorFormErrorLabels} from './serverEditorFormErrors';

/*
 * Props.
 */

interface ServerEditorWrapperProps {
  panelId: SettingsPanelId;
  installedApplication: ApplicationModel;
  server: ServerModel | undefined;
  applicationCredentials: ApplicationCredentialsModel | undefined;
  isReadOnly?: boolean;

  onSaveServer: (unsavedServer: UnsavedServerModel, unsavedCredentials: ApplicationCredentialsModel) => void;

  navigateTo: (resourcePage: SettingsResourcePagesEnum, id?: number) => void;
}

/*
 * Styles.
 */

const StyledOriginLabelWrapperDiv = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin: -8px 0 -8px 6px;
`;

/*
 * Component.
 */

export const ServerEditorWrapper: FC<ServerEditorWrapperProps> = (props) => {
  const {
    panelId,
    installedApplication,
    server,
    applicationCredentials,
    isReadOnly,
    onSaveServer,
    navigateTo,
  } = props;

  const initialEditorState = useMemo(
    () => ServerEditorState.createInitialState(server, applicationCredentials),
    [server, applicationCredentials],
  );

  const {serverEditorState, ...actions} = useServerEditorState(server, applicationCredentials);
  const {serverState, applicationCredentialsState} = serverEditorState;

  const hasDiff = ServerEditorState.hasDiff(initialEditorState, serverEditorState);

  // Reset the credentials at most once after the origin is changed.
  const [willResetCredentials, setWillResetCredentials] = useState(true);

  // Prevent the user from navigating away when there are unsaved changes.
  const {didBlockNavigation, cancelNavigation, unblockNavigation} = useNavigationBlocking(panelId, hasDiff);

  const formErrors = ServerEditorState.computeFormErrors(serverEditorState);

  const editorContext = createServerEditorContext({
    namespace: 'global',
    isReadOnly,
    isEditing: !isUndefined(server),
    hasExpressions: true,
    hasHighlightErrorsFlag: false,
  });

  const onSaveButtonClick = () => {
    if (!onSaveServer) {
      return;
    }

    // Server and credentials are saved independently. We will likely move the credential settings elsewhere eventually.
    const updatedServer = ServerEditorState.toServer(serverEditorState);
    const updatedApplicationCredentials = ServerEditorState.toApplicationCredentials(serverEditorState);

    onSaveServer(updatedServer, updatedApplicationCredentials);

    unblockNavigation();

    navigateTo(SettingsResourcePagesEnum.OVERVIEW);
  };

  const onUpdateOrigin = useCallback(
    (origin: string) => {
      if (!serverState.isWritable) {
        return;
      }

      if (willResetCredentials) {
        setWillResetCredentials(false);
        actions.onUpdateApplicationCredentials(
          DEFAULT_APPLICATION_CREDENTIALS[serverState.authenticationConfig.strategy],
        );
      }

      actions.onUpdateOrigin(origin);
    },
    [serverState.isWritable, serverState.authenticationConfig.strategy, willResetCredentials, actions],
  );

  return (
    <>
      <StyledEditorMain>
        <StyledEditorWrapper>
          <ServerOriginInput
            origin={serverState.origin}
            onUpdate={onUpdateOrigin}
            isReadOnly={editorContext.isReadOnly}
            additionalLabelInfo={
              serverState.isWritable && willResetCredentials && renderServerOriginWarning()
            }
          />
          {
            // A non-local application doesn't need to see authentication config, only the credentials form.
            // The existence of a local application implies a writable server.
            installedApplication.localApplicationId && serverState.isWritable && (
              <ServerAuthenticationConfigCard
                authenticationConfig={serverState.authenticationConfig}
                onUpdate={actions.onUpdateAuthenticationConfig}
                isReadOnly={editorContext.isReadOnly}
              />
            )
          }
          <ApplicationCredentialsCard
            installedApplication={installedApplication}
            credentials={applicationCredentialsState.credentials}
            onUpdate={actions.onUpdateApplicationCredentials}
            // Ensure credentials can be saved
            isReadOnly={false}
            serverOrigin={serverState.origin}
          />
        </StyledEditorWrapper>
        {didBlockNavigation ? (
          <DiscardChangesDialog onCancel={cancelNavigation} onDiscard={unblockNavigation} />
        ) : null}
      </StyledEditorMain>
      <ResourceEditorFooter
        isEditing={editorContext.isEditing}
        // Ensure credentials can be saved
        isReadOnly={false}
        formErrors={formErrors}
        errorLabels={serverEditorFormErrorLabels}
        onCancel={() => navigateTo(SettingsResourcePagesEnum.OVERVIEW)}
        onSave={onSaveButtonClick}
        createLabel={<Trans id="server-editor-footer-create">Create server</Trans>}
      />
    </>
  );
};

/*
 * Helpers.
 */

function renderServerOriginWarning() {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledOriginLabelWrapperDiv>
          <Icon name="warningFilledTriangle" color={theme.palette.orange.shade40} />
          <Trans id="server-origin-update-warning">
            Changing the server origin will clear your credentials.
          </Trans>
        </StyledOriginLabelWrapperDiv>
      )}
    </UserThemeConsumer>
  );
}
